import React from 'react'
import { Link } from 'react-router'
import {
  ShowButton,
  EditButton,
  DeleteButton,
  CreateButton,
} from '@refinedev/antd'
import { useList, type CrudFilter } from '@refinedev/core'
import { Button, Image, Select, Space } from 'antd'
import { PHOTO_KIND_OPTIONS, photoKindFilters, type PhotoKind } from './kindFilter'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, Pill, EmptyHint, type Stat, type Tone } from '../../theme/listkit'
import { Image as ImageIcon, BarChart3, ShieldAlert } from '../../components/icons'

type Photo = Tables<'photos'>
type Album = Tables<'albums'>

type ModerationStatus = 'approved' | 'flagged' | 'rejected' | 'pending'

const moderationTone = (status: string): Tone => {
  if (status === 'approved') return 'green'
  if (status === 'flagged') return 'gold'
  if (status === 'rejected') return 'red'
  return 'neutral' // pending
}

/** The one `groups` shape this page needs — never the full row. */
type GroupKindRow = { id: string; kind: string; name: string }
/** The one `group_meetups` shape this page needs, for a group photo's meetup pill. */
type MeetupTitleRow = { id: string; title: string }

export function PhotoList() {
  const [statusFilter, setStatusFilter] = React.useState<ModerationStatus | 'all'>('all')
  const [kindFilter, setKindFilter] = React.useState<PhotoKind>('all')

  // Groups completion, track b1-media (GRP-DET-1) — a Classroom memory and a
  // group photo are BOTH `photos.group_id` set; only the linked group's
  // `kind` tells them apart, so this page fetches every group's id/kind/name
  // up front (small table) rather than joining per photo. Kept OUTSIDE the
  // `filters` memo below so the kind split is stable across a status-filter
  // change alone.
  const { result: groupsRes } = useList<GroupKindRow>({
    resource: 'groups',
    pagination: { mode: 'off' },
    meta: { select: 'id,kind,name' },
  })
  const { classChapterIds, interestIds, groupNameById } = React.useMemo(() => {
    const rows = (groupsRes?.data ?? []) as GroupKindRow[]
    return {
      classChapterIds: rows.filter((g) => g.kind === 'class_chapter').map((g) => g.id),
      interestIds: rows.filter((g) => g.kind === 'interest').map((g) => g.id),
      groupNameById: Object.fromEntries(rows.map((g) => [g.id, g.name])) as Record<string, string>,
    }
  }, [groupsRes])

  // Custom gallery surface — a BOUNDED page of photos. All three filters are
  // applied server-side (a status eq filter; a group_id in/null filter for
  // the Memories/Group photos/Gallery split) rather than fetching every photo
  // and filtering on the main thread.
  const filters: CrudFilter[] = [
    ...(statusFilter === 'all'
      ? []
      : [{ field: 'moderation_status', operator: 'eq' as const, value: statusFilter }]),
    ...photoKindFilters(kindFilter, { classChapterIds, interestIds }),
  ]
  const { result: photosRes } = useList<Photo>({
    resource: 'photos',
    pagination: { pageSize: 60 },
    sorters: [{ field: 'created_at', order: 'desc' }],
    filters,
    // Only 'memories'/'group' actually depend on the `groups` list above (an
    // `in` filter built from it) — gating the default 'all'/'gallery' views
    // on it too would add a needless delay to the page's own first paint.
    queryOptions: { enabled: kindFilter === 'all' || kindFilter === 'gallery' || groupsRes !== undefined },
  })
  const filteredPhotos = photosRes?.data ?? []

  // Resolve album_id -> albums.name, scoped to the albums referenced by the
  // photos on THIS page (IN filter) instead of mapping over the whole table.
  const albumIds = React.useMemo(
    () => Array.from(new Set(filteredPhotos.map((p) => p.album_id).filter((id): id is string => !!id))),
    [filteredPhotos],
  )
  const { result: albumsRes } = useList<Album>({
    resource: 'albums',
    filters: [{ field: 'id', operator: 'in', value: albumIds }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: albumIds.length > 0 },
  })
  const albumMap = React.useMemo<Record<string, string>>(() => {
    const items = (albumsRes?.data ?? []) as Album[]
    return Object.fromEntries(items.map((a) => [a.id, a.name]))
  }, [albumsRes])

  // Same IN-filter-scoped-to-this-page pattern, for a group photo's linked
  // meetup (GRP-MTG-7) — most photos have none, so this is usually a no-op.
  const meetupIds = React.useMemo(
    () => Array.from(new Set(filteredPhotos.map((p) => p.group_meetup_id).filter((id): id is string => !!id))),
    [filteredPhotos],
  )
  const { result: meetupsRes } = useList<MeetupTitleRow>({
    resource: 'group_meetups',
    filters: [{ field: 'id', operator: 'in', value: meetupIds }],
    pagination: { mode: 'off' },
    meta: { select: 'id,title' },
    queryOptions: { enabled: meetupIds.length > 0 },
  })
  const meetupTitleById = React.useMemo<Record<string, string>>(() => {
    const items = (meetupsRes?.data ?? []) as MeetupTitleRow[]
    return Object.fromEntries(items.map((m) => [m.id, m.title]))
  }, [meetupsRes])

  // Bounded summary deck — a single pre-aggregated row from the admin summary
  // view (total + distinct albums + flagged/rejected) instead of pulling every
  // photo to the client and reducing on the main thread.
  const { result: summaryRes } = useList<{
    total_photos: number | null
    distinct_albums: number | null
    flagged_or_rejected: number | null
  }>({
    resource: 'admin_photos_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const totalPhotos = summary?.total_photos ?? 0
  const distinctAlbums = summary?.distinct_albums ?? 0
  const flaggedOrRejected = summary?.flagged_or_rejected ?? 0

  const stats: Stat[] = [
    { label: 'Total Photos', value: totalPhotos.toLocaleString(), icon: ImageIcon, tone: 'gold' },
    { label: 'Albums', value: distinctAlbums.toLocaleString(), icon: BarChart3, tone: 'jaguar' },
    {
      label: 'Flagged / Rejected',
      value: flaggedOrRejected.toLocaleString(),
      icon: ShieldAlert,
      tone: flaggedOrRejected > 0 ? 'red' : 'neutral',
    },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · Photos',
        description:
          'Every image in the alumni gallery, newest first. Preview shots at a glance, see which album each belongs to, and curate the collection.',
        actions: (
          <Space>
            <Link to="/photos/bulk-upload">
              <Button>Bulk Upload</Button>
            </Link>
            <CreateButton />
          </Space>
        ),
      }}
      stats={stats}
      statColumns={3}
    >
      <SectionCard
        title="Gallery"
        sub="All photos, newest first"
        action={undefined}
      >
        {/* Moderation status filter + Memories / Gallery split */}
        <div style={{ marginBottom: 14 }}>
          <Space wrap>
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as ModerationStatus | 'all')}
              style={{ width: 200 }}
              aria-label="Filter by moderation status"
              options={[
                { label: 'All statuses', value: 'all' },
                { label: 'Approved', value: 'approved' },
                { label: 'Flagged', value: 'flagged' },
                { label: 'Rejected', value: 'rejected' },
                { label: 'Pending', value: 'pending' },
              ]}
            />
            <Select<PhotoKind>
              data-testid="photos-kind-filter"
              value={kindFilter}
              onChange={(v) => setKindFilter(v)}
              style={{ width: 180 }}
              aria-label="Filter by Memories or Gallery"
              options={PHOTO_KIND_OPTIONS}
            />
          </Space>
        </div>

        {filteredPhotos.length === 0 ? (
          <EmptyHint>No photos uploaded yet.</EmptyHint>
        ) : (
          <Image.PreviewGroup>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 16,
              }}
            >
              {filteredPhotos.map((p) => (
                <div
                  key={p.id}
                  style={{
                    border: '1px solid ' + brand.cardLine,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      aspectRatio: '4 / 3',
                      background: brand.paper,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {p.url ? (
                      <Image
                        src={p.url}
                        alt={p.label ?? 'photo'}
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'cover', display: 'block' }}
                        rootClassName="muaa-gallery-img"
                      />
                    ) : (
                      <ImageIcon size={28} color={brand.sub} />
                    )}
                  </div>
                  <div style={{ padding: '11px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div
                      style={{
                        fontFamily: brand.fSans,
                        fontWeight: 700,
                        fontSize: 13,
                        color: brand.ink,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.label || 'Untitled'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {p.album_id ? (
                        <Pill tone="jaguar">{albumMap[p.album_id] ?? p.album_id}</Pill>
                      ) : (
                        <span style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
                          No album
                        </span>
                      )}
                      {/* photos.group_id set: a Classroom memory OR a group photo
                          (Groups completion, track b1-media) — only the linked
                          group's kind tells them apart. A group photo also shows
                          the group's name and, when linked, the meetup (GRP-MTG-7). */}
                      {p.group_id && interestIds.includes(p.group_id) ? (
                        <>
                          <Pill tone="jaguar">{groupNameById[p.group_id] ?? 'Group'}</Pill>
                          {p.group_meetup_id ? (
                            <Pill tone="gold">{meetupTitleById[p.group_meetup_id] ?? 'Meetup'}</Pill>
                          ) : null}
                        </>
                      ) : p.group_id ? (
                        <Pill tone="gold">Memory</Pill>
                      ) : null}
                      <Pill tone={moderationTone(p.moderation_status ?? 'pending')}>
                        {p.moderation_status ?? 'pending'}
                      </Pill>
                    </div>
                    <Space size={4} style={{ marginTop: 2 }}>
                      <ShowButton hideText size="small" recordItemId={p.id} />
                      <EditButton hideText size="small" recordItemId={p.id} />
                      <DeleteButton hideText size="small" recordItemId={p.id} />
                    </Space>
                  </div>
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
        )}
      </SectionCard>
    </ListPage>
  )
}
